import React from 'react';
import { 
  Box, Container, Typography, Button, Paper, Divider, 
  List, ListItem, ListItemIcon, ListItemText, Grid, Alert, AlertTitle, Stack 
} from '@mui/material';
import { 
  PersonRemove, WarningAmber, ErrorOutline, DeleteSweep, 
  FolderDelete, Save, Download, CreditCard, RestorePage, 
  Share, ContactSupport, Email, HeadsetMic, CheckCircle
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

export default function DeleteAccountPolicy() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const dangerGradient = 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'; // Red gradient for delete CTA
  const darkBg = '#070B14';
  const lightBg = '#f8fafc';

  // Helper component for styled lists
  const CustomListItem = ({ text, icon = <CheckCircle sx={{ color: '#FF6A00', fontSize: 18 }} /> }) => (
    <ListItem sx={{ py: 0.5, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {icon}
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }} />
    </ListItem>
  );

  // Helper component for numbered steps
  const StepItem = ({ number, text }) => (
    <ListItem sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}>
      <Box sx={{ 
        width: 24, height: 24, borderRadius: '50%', bgcolor: '#FF6A00', color: 'white', 
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold', mr: 1.5, mt: 0.3, flexShrink: 0 
      }}>
        {number}
      </Box>
      <ListItemText primary={text} primaryTypographyProps={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }} />
    </ListItem>
  );

  // Helper component for section headers
  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 5 }}>
      <Box 
        sx={{ 
          width: 40, height: 40, borderRadius: '10px', 
          background: 'rgba(255,106,0,0.1)', color: '#FF6A00',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight="800" color="#1E293B">
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 PAGE HEADER 🌟 */}
      <Box sx={{ position: 'relative', pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 10 }, bgcolor: darkBg, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <PersonRemove sx={{ fontSize: 50, color: '#ef4444', mb: 2 }} /> {/* Red icon to signify deletion */}
          <Typography variant="h2" fontWeight="900" color="white" sx={{ mb: 2, letterSpacing: '-1px', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Delete Account Policy
          </Typography>
          <Typography variant="h6" color="#94A3B8" fontWeight="500">
            Last updated: January 2025
          </Typography>
        </Container>
      </Box>

      {/* 🌟 MAIN CONTENT 🌟 */}
      <Container maxWidth="md" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ p: { xs: 3, md: 6 }, borderRadius: 4, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 10px 40px rgba(0,0,0,0.03)' }}
        >
          
          <SectionHeader icon={<ContactSupport />} title="1. Overview" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            At <strong>PracharApp</strong>, we respect your right to control your personal data. This policy explains how you can delete your account, what happens to your data when you do, and what alternatives are available.
            <br/><br/>
            We encourage you to contact our support team before deleting your account to discuss any concerns or issues you may have.
          </Typography>

          <SectionHeader icon={<WarningAmber />} title="2. Before You Delete" />
          
          {/* 🌟 CRITICAL WARNING ALERT 🌟 */}
          <Alert 
            icon={<ErrorOutline fontSize="inherit" />} 
            severity="error"
            sx={{ border: '1px solid #fca5a5', bgcolor: '#fef2f2', borderRadius: 3, p: 2.5, mb: 4, '& .MuiAlert-icon': { fontSize: '2rem', mt: 0.5, color: '#dc2626' } }}
          >
            <AlertTitle sx={{ fontWeight: '800', fontSize: '1.1rem', color: '#991b1b', mb: 0.5 }}>Important Notice</AlertTitle>
            <Typography variant="body2" color="#7f1d1d" lineHeight={1.6}>
              Account deletion is <strong>permanent and cannot be undone</strong>. All your data, including created posters, templates, and account history, will be permanently removed from our systems.
            </Typography>
          </Alert>

          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mb={1}>Alternatives to Deletion:</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><CustomListItem text="Account Deactivation: Temporarily disable account" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Cancel Subscription: Keep free account active" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Privacy Settings: Limit data collection" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Data Download: Save data before leaving" /></Grid>
          </Grid>

          <SectionHeader icon={<DeleteSweep />} title="3. How to Delete Your Account" />
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 3, height: '100%', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" fontWeight="800" color="#1E293B" mb={2}>Method 1: Self-Service</Typography>
                <List disablePadding>
                  <StepItem number="1" text="Log in to your PracharApp account" />
                  <StepItem number="2" text="Go to Account Settings" />
                  <StepItem number="3" text="Navigate to 'Privacy & Security'" />
                  <StepItem number="4" text="Click on 'Delete Account'" />
                  <StepItem number="5" text="Follow the confirmation steps" />
                  <StepItem number="6" text="Enter your password to confirm" />
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 3, height: '100%', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" fontWeight="800" color="#1E293B" mb={2}>Method 2: Contact Support</Typography>
                <Typography variant="body2" color="#475569" mb={2}>If you cannot access your account or need help:</Typography>
                <List disablePadding>
                  <CustomListItem text="Email delete@pracharapp.com" />
                  <CustomListItem text="Include registered email address" />
                  <CustomListItem text="Provide a reason (optional)" />
                  <CustomListItem text="Processed within 5 business days" />
                </List>
              </Box>
            </Grid>
          </Grid>

          <SectionHeader icon={<FolderDelete />} title="4. What Gets Deleted" />
          <Typography variant="body1" color="#475569" mb={3}>When you delete your account, the following will be permanently removed:</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="800" color="#1E293B" mb={1}>Personal Info</Typography>
              <Typography variant="body2" color="#64748B" lineHeight={1.6}>Name, email, contact details, profile info, and login credentials.</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="800" color="#1E293B" mb={1}>Creations</Typography>
              <Typography variant="body2" color="#64748B" lineHeight={1.6}>All created posters, videos, saved templates, favorites, and uploads.</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="800" color="#1E293B" mb={1}>Usage Data</Typography>
              <Typography variant="body2" color="#64748B" lineHeight={1.6}>Activity logs, search history, preferences, and analytics data.</Typography>
            </Grid>
          </Grid>

          <SectionHeader icon={<Save />} title="5. What We Retain" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            For legal and security purposes, we may retain certain information (typically for 7 years):
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><CustomListItem text="Transaction records for tax/accounting" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Data required for legal proceedings" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Anonymized data for security analysis" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Fraud prevention & abuse records" /></Grid>
          </Grid>

          <SectionHeader icon={<Download />} title="6. Data Download" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            We strongly recommend downloading your data (posters, history, settings) before deleting your account. To do this: 
            Go to <strong>Settings</strong> {'>'} <strong>Download My Data</strong>. We'll email you a link within 24 hours.
          </Typography>

          <SectionHeader icon={<CreditCard />} title="7. Subscription & Billing" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            If you have an active subscription, it will be immediately cancelled upon deletion. No refunds are provided for unused time. <br/>
            <strong>Tip:</strong> Cancel your subscription first, then delete your account at the end of your billing cycle.
          </Typography>

          <SectionHeader icon={<RestorePage />} title="8. Reactivation Policy" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            Once deleted, an account <strong>cannot be recovered</strong>. To use PracharApp again, you must create a brand new account and start fresh.
          </Typography>

          <SectionHeader icon={<Share />} title="9. Third-Party Services" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            Deleting your account does not delete content you've already shared on social media, downloaded to your phone, or shared with third-party integrations. You must manage those manually.
          </Typography>

        </Paper>
      </Container>

      {/* 🌟 BOTTOM CTA SECTION (WITH DANGER BUTTON) 🌟 */}
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Box 
          sx={{ 
            background: 'white', borderRadius: 4, p: { xs: 4, md: 5 }, 
            textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.03)'
          }}
        >
          <Typography variant="h4" fontWeight="900" color="#1E293B" mb={1}>Still Considering Deletion?</Typography>
          <Typography variant="subtitle1" color="#64748B" mb={4}>
            Our support team is here to help resolve any issues you may have before you leave.
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            {/* Primary Action - Safe */}
            <Button 
              component="a" href="mailto:support@pracharapp.com"
              variant="contained" startIcon={<HeadsetMic />}
              sx={{ 
                background: primaryGradient, color: 'white', px: 4, py: 1.5, 
                borderRadius: '50px', fontWeight: '800', fontSize: '1rem',
                boxShadow: '0 8px 20px rgba(255, 106, 0, 0.25)',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 25px rgba(255, 106, 0, 0.35)' },
                transition: 'all 0.3s'
              }}
            >
              Contact Support
            </Button>
            
            {/* Secondary Action - Danger */}
            <Button 
              component="a" href="mailto:delete@pracharapp.com"
              variant="outlined" startIcon={<DeleteSweep />}
              color="error" // MUI Red color
              sx={{ 
                px: 4, py: 1.5, borderRadius: '50px', fontWeight: '800', fontSize: '1rem', borderWidth: 2,
                '&:hover': { borderWidth: 2, bgcolor: '#fef2f2', transform: 'translateY(-2px)' },
                transition: 'all 0.3s'
              }}
            >
              Delete Account
            </Button>
          </Stack>
        </Box>
      </Container>

    </Box>
  );
}